/**
 * Renders a funding-sources screen in the consumer flow's chrome: the step's kicker and progress come
 * from the consumer step, the screen's title (its icon beside it), body and actions go into `StepFrame`,
 * and its actions land in the consumer footer. A second forward choice stays visible on phones when the screen has one.
 */
import type { FundingScreenAction, FundingScreenParts } from '@/features/funding-sources';
import { StepFrame } from './step-frame';
import type { StepAction } from './step-footer';
import type { ConsumerStepId } from '../lib/steps';

function toStepAction(action: FundingScreenAction | undefined): StepAction | undefined {
  if (!action) return undefined;
  return {
    label: action.label,
    onClick: action.onSelect,
    disabled: action.disabled,
    loading: action.busy,
  };
}

export function FundingScreenFrame({
  step,
  parts,
}: {
  step: ConsumerStepId;
  parts: FundingScreenParts;
}) {
  return (
    <StepFrame
      step={step}
      title={parts.title}
      titleBadge={
        parts.icon && (
          // The screens draw their icon for a row of its own; beside the title it takes the size of
          // Your Plan's tick.
          <span className="flex [&>*]:size-9 [&>*]:p-0 [&_svg]:size-5">{parts.icon}</span>
        )
      }
      subtitle={parts.subtitle || undefined}
      onBack={parts.backAction?.onSelect}
      primary={toStepAction(parts.primaryAction)}
      secondary={toStepAction(parts.secondaryAction)}
      secondaryOnMobile={!!parts.secondaryAction}
    >
      {parts.content}
    </StepFrame>
  );
}
